import S, { DataSignal } from 's-js';
import { lift } from 's-array';
import { IAjax, Client } from 'lib/Ajax';
import { SaveDraftObject } from 'lib/autoSave';
import { DraftObject } from 'lib/draftSignals';
import { Mutex } from 'lib/mutex';

import { CoordinationCard } from 'data/models/CoordinationCard';
import { CoordinationCardRights, PortCallRights } from 'server/enums/Rights';
import { CoordinationProvider } from 'data/models/CoordinationProvider';
import { CoordinationProviderCurrency } from 'server/enums/CoordinationProviderCurrency';

import { Entrypoints } from 'core/app/entrypoints';

export interface CoordinationCardDetailsActionsTabParams {
    CoordinationCard : CoordinationCard;
    DismissDetails : () => void;
    Rights : () => CoordinationCardRights;
}

enum CoordinationCardAction {
    CancelCoordination = 'cancel-coordination',
    ChangeProvider = 'change-provider'
}

export type CoordinationCardDetailsActionsTab = ReturnType<typeof CoordinationCardDetailsActionsTab>;
export function CoordinationCardDetailsActionsTab(
    ajax : IAjax,
    entrypoints : Entrypoints,
    {
        CoordinationCard,
        DismissDetails,
        Rights,
    } : CoordinationCardDetailsActionsTabParams
) {
    const
        client = ajax.Url<CoordinationCard, unknown>(() => CoordinationCard.Url() ?? '', { useCache: true }),
        // A mutex to prevent overlapping attempts to save changes, especially to the same entity
        saveMutex = new Mutex(),
        selectedAction = S.value<CoordinationCardAction | null>(null),
        actionIsSelected = (action : CoordinationCardAction) : DataSignal<boolean> => (val? : boolean) => {
            if (val === undefined) {
                return selectedAction() === action;
            } else {
                const isSelected = S.sample(selectedAction) === action;
                if (val !== isSelected) selectedAction(val ? action : null);
                return val;
            }
        },
        CancelCoordination = CancelCoordinationAction(
            DismissDetails,
            Rights,
            client,
            saveMutex,
            actionIsSelected(CoordinationCardAction.CancelCoordination)
        ),
        ChangeProvider = ChangeProviderAction(
            CoordinationCard,
            DismissDetails,
            Rights,
            client,
            saveMutex,
            actionIsSelected(CoordinationCardAction.ChangeProvider)
        ),
        HasAnyAvailableActions = () =>
            CancelCoordination.IsAvailable()
         || ChangeProvider.AvailableProviders().length > 0,
        Loading = () =>
            CancelCoordination.IsPerforming()
         || ChangeProvider.IsPerforming();

    return {
        CoordinationCard,
        CancelCoordination,
        ChangeProvider,
        HasAnyAvailableActions,
        Loading
    };
}

export type CancelCoordinationAction = ReturnType<typeof CancelCoordinationAction>;
function CancelCoordinationAction(
    DismissDetails : () => void,
    Rights : () => CoordinationCardRights,
    client : Client<CoordinationCard, unknown>,
    saveMutex : Mutex,
    IsSelected : DataSignal<boolean>
) {
    const
        IsAvailable = () => (Rights() & CoordinationCardRights.Cancel) !== 0,
        IsPerforming = S.value(false);

    return {
        IsSelected,
        IsAvailable,
        IsPerforming: () => IsPerforming(),
        Perform
    };

    async function Perform() {
        if (IsPerforming()) return;
        IsPerforming(true);
        try {
            const guard = await saveMutex.acquire();
            try {
                await client.Delete();
                DismissDetails();
            } finally {
                guard.release();
            }
        } finally {
            IsPerforming(false);
        }
    }
}

export type ChangeProviderAction = ReturnType<typeof ChangeProviderAction>;
function ChangeProviderAction(
    CoordinationCard : CoordinationCard,
    DismissDetails : () => void,
    Rights : () => CoordinationCardRights,
    client : Client<CoordinationCard, unknown>,
    saveMutex : Mutex,
    IsSelected : DataSignal<boolean>
) {
    const
        draft = DraftObject(CoordinationCard, ['ProviderId']),
        AvailableProviders =
            lift(() => (Rights() & CoordinationCardRights.ChangeProvider) === 0 ?
                [] :
                CoordinationCard.Coordination()?.Providers() ?? []
            )
            .filter(p =>
                p.Currency() === CoordinationProviderCurrency.Current &&
                p.ProviderId() !== CoordinationCard.ProviderId()
            )
            .sort(CoordinationProvider.DisplayOrder),
        IsPerforming = S.value(false);

    S(() => {
        if (IsSelected()) {
            if (AvailableProviders().length > 0) {
                draft.object.ProviderId(AvailableProviders()[0].ProviderId());
            }
        } else {
            draft.methods.checkpoint().revert();
        }
    });

    return {
        IsSelected,
        Draft: draft.object,
        AvailableProviders,
        IsPerforming: () => IsPerforming(),
        Perform
    };

    async function Perform() {
        if (IsPerforming()) return;
        IsPerforming(true);
        try {
            const guard = await saveMutex.acquire();
            try {
                await SaveDraftObject(draft, client);
                DismissDetails();
            } finally {
                guard.release();
            }
        } finally {
            IsPerforming(false);
        }
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/coordinationcarddetails/actions.ts